import { Component, OnInit } from '@angular/core';
import { OwlOptions } from 'ngx-owl-carousel-o';
import emailjs, { type EmailJSResponseStatus } from '@emailjs/browser';

/**
 * Slide interface for carousel slides
 */
interface Slide {
  title: string;
  subtitle: string;
  image: string;
}

/**
 * Fact interface for fun facts section
 */
interface Fact {
  icon: string;
  title: string;
  value: number;
  iconBg: 'primary' | 'white';
  textColor: 'white' | 'primary';
  position: 'left' | 'center' | 'right';
}

@Component({
  selector: 'app-home-one',
  templateUrl: './home-one.component.html',
  styleUrl: './home-one.component.css'
})
export class HomeOneComponent implements OnInit {
  // Owl Slider Options
  customOptions: OwlOptions = {
    loop: true,
    dots: true,
    margin: 0,
    nav: true,
    autoplay: true,
    autoplaySpeed: 2000,
    navSpeed: 2000,
    items: 1,
    navText: ['<i class="bi bi-arrow-left"></i>', '<i class="bi bi-arrow-right"></i>'],
  };

  // Carousel slides data
  slideStore: Slide[] = [
    {
      title: 'Building Brands With Impactful Ideas',
      subtitle: 'Creative Agency',
      image: 'assets/images/carousel-1.jpg'
    },
    {
      title: 'Design That Delivers',
      subtitle: 'Creative Solutions for Digital Success',
      image: 'assets/images/carousel-2.jpg'
    }
  ];

  // Fun Facts data
  facts: Fact[] = [
    {
      icon: 'bi-emoji-smile',
      title: 'Satisfied Clients',
      value: 4500,
      iconBg: 'white',
      textColor: 'white',
      position: 'left'
    },
    {
      icon: 'bi-check2-circle',
      title: 'Completed Projects',
      value: 354,
      iconBg: 'primary',
      textColor: 'primary',
      position: 'center'
    },
    {
      icon: 'bi-trophy',
      title: 'Industry Awards',
      value: 75,
      iconBg: 'white',
      textColor: 'white',
      position: 'right'
    }
  ];

  constructor() {}

  ngOnInit(): void {
    // Component initialization
  }

  /**
   * Get the background class for the fact container
   * @param position Position of the fact (left, center, right)
   * @returns CSS classes for background styling
   */
  getBackgroundClass(position: string): string {
    switch (position) {
      case 'left':
        return 'bg-primary radius-left';
      case 'center':
        return 'bg-light';
      case 'right':
        return 'bg-primary radius-right';
      default:
        return 'bg-primary';
    }
  }

  /**
   * Get the text color class for the fact title
   * @param textColor Color of the text (white or primary)
   * @returns CSS classes for text color
   */
  getTextColorClass(textColor: string): string {
    return textColor === 'white' ? 'text-white' : 'text-primary';
  }

  /**
   * Get the icon background color class
   * @param iconBg Background color (primary or white)
   * @returns CSS classes for icon background
   */
  getIconBgClass(iconBg: string): string {
    return iconBg === 'primary' ? 'bg-primary' : 'bg-white';
  }

  /**
   * Get the icon color class
   * @param iconBg Background color (primary or white)
   * @returns CSS classes for icon color
   */
  getIconColorClass(iconBg: string): string {
    return iconBg === 'primary' ? 'text-white' : 'text-primary';
  }

  //Services
  leftFeatures = [
    {
      icon: 'bi-lightbulb',
      title: 'Creative Vision',
      desc: 'We craft unique ideas and bring them to life through bold visual storytelling and branding.'
    },
    {
      icon: 'bi-trophy',
      title: 'Award-Winning Work',
      desc: 'Recognized by top design institutions for our impactful and engaging digital campaigns.'
    }
  ];

  rightFeatures = [
    {
      icon: 'bi-people',
      title: 'Experienced Team',
      desc: 'Our talented designers, developers, and strategists collaborate to build memorable brands.'
    },
    {
      icon: 'bi-headset',
      title: 'Reliable Support',
      desc: 'We offer dedicated support and communication throughout your project and beyond launch.'
    }
  ];

  //Services Two
  services = [
    {
      icon: 'bi-brush',
      title: 'Brand Design',
      desc: 'We shape visual identities that express your story and connect emotionally with your audience.'
    },
    {
      icon: 'bi-laptop',
      title: 'UI/UX Design',
      desc: 'Crafting intuitive, elegant, and user-friendly interfaces that enhance engagement and usability.'
    },
    {
      icon: 'bi-palette',
      title: 'Graphic Design',
      desc: 'From print to digital, our creatives deliver high-impact visuals aligned with your brand ethos.'
    },
    {
      icon: 'bi-phone',
      title: 'App UI Design',
      desc: 'Modern mobile-first interface designs that are visually consistent and user-centric.'
    },
    {
      icon: 'bi-megaphone',
      title: 'Content Strategy',
      desc: 'We create engaging content strategies that drive traffic and reflect your brand personality.'
    }
  ];

  //Pricing Table
  plans = [
    {
      name: 'Starter Plan',
      subtitle: 'Ideal for Freelancers & Startups',
      price: 59,
      highlight: false,
      features: [
        { text: 'Logo Design', available: true },
        { text: '1 Page Website', available: true },
        { text: 'Social Media Kit', available: false },
        { text: 'Revisions', available: false }
      ]
    },
    {
      name: 'Professional Plan',
      subtitle: 'Perfect for Growing Brands',
      price: 129,
      highlight: true,
      features: [
        { text: 'Logo + Brand Kit', available: true },
        { text: 'Up to 5 Web Pages', available: true },
        { text: 'Responsive Design', available: true },
        { text: '3 Revisions', available: false }
      ]
    },
    {
      name: 'Enterprise Plan',
      subtitle: 'For Agencies & Large Businesses',
      price: 249,
      highlight: false,
      features: [
        { text: 'Full Branding Package', available: true },
        { text: '10+ Web Pages', available: true },
        { text: 'SEO Optimized', available: true },
        { text: 'Unlimited Revisions', available: true }
      ]
    }
  ];

  // Contact Form
  formData = {
    user_name: '',
    user_email: '',
    message: ''
  };

  // EmailJS service configuration
  private serviceId = 'helifax-form';  // Replace with your EmailJS Service ID
  private templateId = 'helifax-form';  // Replace with your EmailJS Template ID
  private publicKey = 'dOfh1ImCGDZCO9GJb';  // Replace with your EmailJS Public Key

  // Method to send email on form submission
  public sendEmail(event: Event): void {
    event.preventDefault();

    // Send email using EmailJS
    emailjs.sendForm(this.serviceId, this.templateId, event.target as HTMLFormElement, this.publicKey)
      .then(
        (response: EmailJSResponseStatus) => {
          alert('Message sent successfully. Thank you, we will get back to you soon!');
          this.resetForm();
        },
        (error: EmailJSResponseStatus) => {
          console.error('Failed to send message. Error:', error.text);
          alert('Failed to send the message. Please try again later.');
        }
      );
  }

  // Method to reset the form after submission
  private resetForm(): void {
    this.formData = {
      user_name: '',
      user_email: '',
      message: ''
    };
  }

  //Testimonials
  testimonialCarousel: OwlOptions = {
    loop: true,
    dots: true,
    margin:0,
    nav:false,
    autoplay:true,
    autoplaySpeed: 2000,
    navSpeed: 2000,
    items: 3,
    center: true,
    navText: ['<i class="bi bi-arrow-left"></i>', '<i class="bi bi-arrow-right"></i>'],
  }

  clients = [
    {
      title: "Sarah Malik",
      subtitle: "Marketing Manager",
      image: "testimonial-1.jpg",
      desc:"The branding and website design exceeded our expectations. Their team really understood our vision and delivered creative solutions on time."
    },

    {
      title: "James Patel",
      subtitle: "Startup Founder",
      image: "testimonial-2.jpg",
      desc:"Outstanding creativity! They helped us launch a bold digital presence that truly reflects our brand. Highly recommended for startups."
    },

    {
      title: "Maria Chen",
      subtitle: "Creative Director",
      image: "testimonial-3.jpg",
      desc:"Professional, responsive, and visually stunning design work. It felt like having an in-house design team who truly cared about our goals."
    }

  ];

  //Team Members
  teamMembers = [
    {
      name: "Sarah Doe",
      role: "Creative Director",
      image: "team-1.jpg",
      socials: [
        { icon: "fab fa-x-twitter", link: "/our-team" },
        { icon: "fab fa-facebook-f", link: "/our-team" },
        { icon: "fab fa-instagram", link: "/our-team" },
        { icon: "fab fa-linkedin-in", link: "/our-team" }
      ]
    },
    {
      name: "Aaron Doe",
      role: "Lead Web Developer",
      image: "team-2.jpg",
      socials: [
        { icon: "fab fa-x-twitter", link: "/our-team" },
        { icon: "fab fa-facebook-f", link: "/our-team" },
        { icon: "fab fa-instagram", link: "/our-team" },
        { icon: "fab fa-linkedin-in", link: "/our-team" }
      ]
    },
    {
      name: "Anis Doe",
      role: "UI/UX Designer",
      image: "team-3.jpg",
      socials: [
        { icon: "fab fa-x-twitter", link: "/our-team" },
        { icon: "fab fa-facebook-f", link: "/our-team" },
        { icon: "fab fa-instagram", link: "/our-team" },
        { icon: "fab fa-linkedin-in", link: "/our-team" }
      ]
    }
  ];

  //Blogs
  blogs = [
    {
      image: "blog-1.jpg",
      category: "Web Design",
      author: "Nadia Farooq",
      date: "08 Jun, 2024",
      title: "5 Web Design Trends to Watch in 2025",
      desc: "Discover the latest UI/UX techniques and creative inspirations that are shaping modern websites."
    },
    {
      image: "blog-2.jpg",
      category: "Digital Marketing",
      author: "Ali Raza",
      date: "22 May, 2025",
      title: "Boost SEO with These Proven Content Strategies",
      desc: "Learn how strategic content planning and SEO optimization can help drive organic traffic."
    },
    {
      image: "blog-3.jpg",
      category: "App Development",
      author: "Sana Qureshi",
      date: "15 May, 2025",
      title: "Essential Features Every Modern App Must Include",
      desc: "From user onboarding to dark mode, here are must-have features for high-performing mobile apps."
    }
  ];

  // OWl Slider
  brandsCarousel: OwlOptions = {
    loop: true,
    dots: false,
    margin:50,
    nav:false,
    autoplay:true,
    autoplaySpeed: 2000,
    navSpeed: 2000,
    items: 6,
    navText: ['<i class="bi bi-arrow-left-short"></i>', '<i class="bi bi-arrow-right-short"></i>'],
  }

  brandStore = [
    {img: "vendor-1.jpg"},
    {img: "vendor-2.jpg"},
    {img: "vendor-3.jpg"},
    {img: "vendor-4.jpg"},
    {img: "vendor-5.jpg"},
    {img: "vendor-6.jpg"}
  ];
}
